<template>
    <div v-if="checkRole(project.id, 'project:users:view')" class="users-section">
        <div class="users-section__stack">
            <div class="users-section__head">
                <h2 class="section-title">Участники</h2>
                <HintIcon hintId="users" :size="17"></HintIcon>
            </div>
            <div class="toolbar-row" v-if="checkRole(project.id, 'project:users:edit')">
                <div class="toolbar-left">
                    <AssignPermissionBtn :selectedUsers="selectedUsers" :projectId="project.id">
                    </AssignPermissionBtn>
                    <button
                        class="toolbtn"
                        type="button"
                        :disabled="usersBtnsDisable"
                        @click="deleteUsers"
                    >
                        <v-icon size="16" :color="usersBtnsDisable ? '#A5A9B3' : '#1A2040'">mdi-close-circle-outline</v-icon>
                        <span>Удалить</span>
                    </button>
                </div>
                <v-btn
                    elevation="0"
                    color="mainNew"
                    class="white--text btn-w-icon"
                    @click="openAddUserDialog"
                >
                    <v-icon class="mr-5px">mdi-plus</v-icon>
                    Добавить участника
                </v-btn>
            </div>
        </div>
        <div class="table-block">
            <v-data-table
                ref="userTable"
                @input="selectUser($event)"
                :headers="headers"
                :value="selectedUsers"
                :items="tableUsers"
                :single-select="false"
                @toggle-select-all="selectAllUsers"
                :hide-default-footer="pagination"
                item-key="id"
                selectable-key="isSelectable"
                :show-select="
                    project.users.length > 1 && checkRole(project.id, 'project:users:edit')
                "
                :mobile-breakpoint="0"
                disable-sort
                :footer-props="footerProps"
                class="user-table"
            >
                <template v-slot:[`item.name`]="{ item }">
                    <div class="usr">
                        <v-avatar
                            v-if="!item.isPending && item.avatar"
                            size="34"
                            class="usr__av"
                        >
                            <v-img
                                transition="slide-x-transition"
                                :src="item.avatar"
                                :alt="item.email"
                            />
                        </v-avatar>
                        <span
                            v-else
                            class="usr__av usr__av--fallback"
                            :style="{ background: getAvatarColor(item) }"
                        >
                            {{ getInitials(item) }}
                        </span>
                        <span
                            class="usr__nm"
                            :title="item.name || item.email"
                        >{{ item.name || item.email }}</span>
                    </div>
                </template>
                <template v-slot:[`item.role`]="{ item }">
                    <span
                        class="role"
                        :title="item.isPending ? `${item.role.name} (Пользователь не зарегистрирован)` : item.role.name"
                    >
                        <template v-if="item.isPending">
                            {{ item.role.name }} (Пользователь не зарегистрирован)
                        </template>
                        <template v-else>{{ item.role.name }}</template>
                    </span>
                </template>
                <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
                    <v-simple-checkbox
                        :value="item.id == $auth.user.sub ? false : isSelected"
                        v-if="
                            item.id != $auth.user.sub &&
                            checkRole(project.id, 'project:users:edit')
                        "
                        :disabled="item.id == $auth.user.sub"
                        :readonly="item.id == $auth.user.sub"
                        :ripple="false"
                        color="mainNew"
                        @input="select($event)"
                    ></v-simple-checkbox>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu :close-on-content-click="false" :nudge-width="200" offset-x offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                icon
                                small
                                class="actions-btn"
                                v-bind="attrs"
                                v-on="on"
                                v-if="checkRole(project.id, 'project:users:edit')"
                                :disabled="!checkRole(project.id, 'project:users:edit')"
                            >
                                <v-icon size="20">mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>

                        <v-card min-width="198px">
                            <v-list dense>
                                <div
                                    v-if="
                                        checkRole(project.id, 'project:users:edit') &&
                                        item.id !== $auth.user.sub
                                    "
                                >
                                    <v-list-item class="mx-3">
                                        <span class="black--text text-subtitle-2">ПРАВА</span>
                                    </v-list-item>

                                    <v-list-item
                                        v-for="role in roles"
                                        :key="role.id"
                                        class="text-btn-wrapper"
                                        @click="reAssignPermission(item.id, role.id)"
                                    >
                                        <span class="mx-3 my-2">{{ role.name }}</span>
                                    </v-list-item>

                                    <v-divider class="my-2"></v-divider>
                                </div>
                                <v-list-item
                                    class="text-btn-wrapper"
                                    v-if="checkRole(project.id, 'project:users:edit')"
                                    @click="changeUserName(item.id)"
                                >
                                    <span class="mx-3 my-2">Изменить имя</span>
                                </v-list-item>
                                <v-list-item
                                    v-if="
                                        checkRole(project.id, 'project:users:edit') &&
                                        item.id !== $auth.user.sub
                                    "
                                    class="text-btn-wrapper"
                                    @click="deleteUser(item.id)"
                                >
                                    <span class="mx-3 my-2">Удалить</span>
                                </v-list-item>
                            </v-list>
                        </v-card>
                    </v-menu>
                </template>
            </v-data-table>
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import { checkRole } from '../../helpers/roles'
import { mapState } from 'vuex'
import HintIcon from '../HintIcon.vue';
import AssignPermissionBtn from '../buttons/AssignPermissionBtn.vue';
import { avatarColor, initials } from '@/helpers/gcb2/chatChannels'

export default Vue.extend({
    name: 'UserList',
    props: {
        selectedUsers: {type: Array, required: true}
    },
    components: {HintIcon, AssignPermissionBtn},
    data: () => ({
        headers: [
            {
                text: 'Участник',
                value: 'name',
                sortable: false,
                class: 'user-col-name',
                cellClass: 'user-col-name',
            },
            {
                text: 'Права',
                value: 'role',
                sortable: false,
                class: 'user-col-role',
                cellClass: 'user-col-role',
            },
            {
                text: '',
                value: 'actions',
                sortable: false,
                align: 'end',
                width: '48px',
                class: 'user-col-actions',
                cellClass: 'user-col-actions',
            },
        ],
        footerProps: {
            'items-per-page-options': [10, 15, 50],
            'items-per-page-text': 'На стр.:',
        },
    }),
    computed: {
        ...mapState(['roles']),
        project() {
            const projectId = this.$router.currentRoute.params.id
            return this.$store.getters.projectById(projectId)
        },
        currentUserId() {
            return this.$auth.user.sub
        },
        // Текущий пользователь нельзя выбирать — иначе Vuetify never считает страницу everyItem=true
        tableUsers() {
            return (this.project.users || []).map((user) => ({
                ...user,
                isSelectable: user.id !== this.currentUserId,
            }))
        },
        usersBtnsDisable() {
            if (this.selectedUsers.length > 0) {
                return false
            } else {
                return true
            }
        },
        pagination() {
            if (this.project.users.length < 10) {
                return true
            } else {
                return false
            }
        },
        selectedUserIds() {
            return this.selectedUsers.map((user) => user.id)
        },
    },
    methods: {
        checkRole,
        getInitials(item) {
            return initials(item.name || item.email)
        },
        getAvatarColor(item) {
            return avatarColor(String(item.id || item.email || item.name || ''))
        },
        editUsers(value) {
            this.$emit('edit-users', value)
        },
        selectUser(e) {
            let selectedItems = [...e].filter((el) => el.id !== this.currentUserId)
            this.editUsers(selectedItems)
        },
        async deleteUsers() {
            for (const userId of this.selectedUserIds) {
                await this.$store.dispatch('project/unAssignPermission', {
                    projectId: this.project.id,
                    userId,
                })
                this.$store.commit('removeUser', {
                    projectId: this.project.id,
                    userId,
                })
                this.editUsers([])
            }
        },
        openAddUserDialog() {
            this.$store.commit('project/updateIsAddUserDialog', true)
        },
        selectAllUsers(e) {
            const pageItems = e.items || []
            const selectablePageItems = pageItems.filter((el) => el.isSelectable !== false)

            if (e.value === false) {
                const pageIdSet = new Set(pageItems.map((el) => el.id))
                this.editUsers(this.selectedUsers.filter((el) => !pageIdSet.has(el.id)))
                return
            }

            // Как раньше: «выбрать все» на странице заменяет выделение на пользователей этой страницы
            this.editUsers(selectablePageItems)
        },
        async reAssignPermission(userId, roleId) {
            await this.$store.dispatch('project/reAssignPermission', {
                projectId: this.project.id,
                userIdtoChange: userId,
                roleId,
            })
        },
        async changeUserName(userId) {
            this.$store.commit('project/setCurrentUserIdToRename', userId)
            this.$store.commit('project/setIsRenameUserDialog', true)
        },
        async deleteUser(userId) {
            await this.$store.dispatch('project/unAssignPermission', {
                projectId: this.project.id,
                userId,
            })
            this.$store.commit('removeUser', {
                projectId: this.project.id,
                userId,
            })
        },
    }
    
})
</script>
<style lang="sass" scoped>
@import '@/vars.sass'

.users-section
    max-width: 1016px

.users-section__stack
    display: flex
    flex-direction: column
    gap: 14px

.users-section__head
    display: flex
    align-items: center
    gap: 9px

.section-title
    font-family: $font-family-base
    font-size: 21px
    font-weight: 900
    letter-spacing: -.025em
    color: $dark-new
    margin: 0
    line-height: 1.2

.toolbar-row
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    flex-wrap: wrap

.toolbar-left
    display: flex
    align-items: center
    gap: 10px
    flex-wrap: wrap

.toolbtn
    height: 34px
    display: inline-flex
    align-items: center
    gap: 8px
    padding: 0 16px
    border-radius: 4px
    border: 1px solid #BFC5D3
    background: #F6F7FA
    color: $text-color-new
    font-family: $font-family-base
    font-size: 11px
    font-weight: 800
    letter-spacing: .09em
    text-transform: uppercase
    cursor: pointer
    box-shadow: 0 1px 0 rgba(255, 255, 255, .85) inset
    transition: border-color .15s, color .15s, background .15s

    &:hover:not(:disabled)
        border-color: $dark-new

    &:disabled
        color: #A5A9B3
        opacity: .55
        cursor: not-allowed

.table-block
    margin-top: 14px
    background: #fff
    border: 1px solid $border-new
    border-radius: 16px
    overflow: hidden
    box-shadow: 0 1px 2px rgba(26, 26, 46, .03)

.usr
    display: flex
    align-items: center
    gap: 12px
    min-width: 0
    max-width: 100%

    &__av
        flex-shrink: 0

    &__av--fallback
        width: 34px
        height: 34px
        border-radius: 50%
        display: inline-flex
        align-items: center
        justify-content: center
        color: #fff
        font-family: $font-family-base
        font-weight: 800
        font-size: 13px
        line-height: 1

    &__nm
        min-width: 0
        color: $text-color-new
        font-size: 13.5px
        line-height: 1.3
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

.role
    display: block
    max-width: 100%
    font-family: $font-family-base
    font-size: 11px
    font-weight: 700
    letter-spacing: .03em
    text-transform: uppercase
    color: $muted-new
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

    &--own
        color: $main-new

.actions-btn
    color: $muted-new !important

.user-table
    background: transparent !important
    border-radius: 0

    ::v-deep
        .v-data-table__wrapper
            border-radius: 0
            overflow-x: hidden

        table
            table-layout: fixed
            width: 100%
            border-collapse: collapse

        colgroup col:first-child,
        thead th.user-col-select,
        tbody td.user-col-select
            width: 52px

        thead th
            text-align: left !important
            font-family: $font-family-base
            font-size: 11px !important
            font-weight: 800 !important
            letter-spacing: .04em
            text-transform: uppercase !important
            color: $muted-new !important
            background: $app-bcg-new !important
            padding: 13px 16px !important
            border-bottom: 1px solid $border-new !important
            height: auto !important
            white-space: nowrap

        // Select column (auto-injected by Vuetify when show-select)
        thead th:first-child:not(.user-col-name):not(.user-col-role):not(.user-col-actions),
        tbody td:first-child:not(.user-col-name):not(.user-col-role):not(.user-col-actions)
            width: 52px !important
            min-width: 52px !important
            max-width: 52px !important
            padding: 10px 8px 10px 14px !important
            overflow: visible !important
            text-align: center !important
            box-sizing: border-box

        .user-col-name
            width: auto
            overflow: hidden

        .user-col-role
            width: 140px
            overflow: hidden

        .user-col-actions
            width: 48px
            min-width: 48px
            max-width: 48px
            padding-left: 4px !important
            padding-right: 12px !important
            text-align: end !important
            overflow: visible !important

        tbody tr:hover
            background: transparent !important

        tbody tr:hover td
            background: #FAFBFD !important

        tbody td
            padding: 13px 16px !important
            border-bottom: 1px solid $border-light-new !important
            font-size: 13.5px
            height: auto !important
            vertical-align: middle

        tbody td.user-col-name,
        tbody td.user-col-role
            overflow: hidden

        tbody tr:last-child td
            border-bottom: none !important

        .v-data-table__checkbox,
        .v-simple-checkbox
            margin: 0 !important

        .v-data-table__checkbox .v-icon,
        .v-simple-checkbox .v-icon
            color: $muted-light-new !important

        .v-data-table__checkbox .v-icon.primary--text,
        .v-simple-checkbox .v-icon.primary--text,
        .v-data-table__checkbox .v-icon.mainNew--text,
        .v-simple-checkbox .v-icon.mainNew--text
            color: $main-new !important

        .v-data-footer
            border-top: 1px solid $border-new
            flex-wrap: nowrap
            justify-content: flex-end
            align-items: center
            margin: 0
            padding: 10px 16px

        .v-data-footer__select
            margin-right: auto !important
            justify-content: flex-start
            white-space: nowrap

        .v-data-footer__pagination
            margin: 0 4px 0 12px !important
            white-space: nowrap
            flex-shrink: 0

        .v-data-footer__icons-before,
        .v-data-footer__icons-after
            margin: 0 !important
            flex-shrink: 0

@media (max-width: 700px)
    .toolbar-row
        gap: 10px

    .toolbtn
        height: 32px
        padding: 0 12px
        font-size: 10px
        gap: 6px

    .usr
        gap: 8px

        &__av--fallback
            width: 28px
            height: 28px
            font-size: 11px

        &__nm
            font-size: 12px

    .role
        font-size: 10px
        letter-spacing: .02em

    .user-table
        ::v-deep
            thead th
                font-size: 10px !important
                padding: 10px 8px !important

            tbody td
                padding: 10px 8px !important
                font-size: 12px

            thead th:first-child:not(.user-col-name):not(.user-col-role):not(.user-col-actions),
            tbody td:first-child:not(.user-col-name):not(.user-col-role):not(.user-col-actions)
                width: 44px !important
                min-width: 44px !important
                max-width: 44px !important
                padding: 8px 4px 8px 10px !important

            .user-col-role
                width: 110px
                padding-left: 6px !important
                padding-right: 6px !important

            .user-col-actions
                width: 40px
                min-width: 40px
                max-width: 40px
                padding-left: 0 !important
                padding-right: 8px !important

            .usr .v-avatar
                width: 28px !important
                height: 28px !important
                min-width: 28px !important

            .v-data-footer
                flex-wrap: nowrap !important
                justify-content: flex-end
                padding: 8px 8px !important
                min-height: 44px

            .v-data-footer__select
                flex: 0 1 auto
                min-width: 0
                margin: 0 auto 0 0 !important
                font-size: 12px

                .v-input
                    margin-top: 0
                    padding-top: 0
                    font-size: 12px

                .v-select__selections
                    font-size: 12px

                .v-input__slot
                    min-height: 28px !important

                .v-select
                    font-size: 12px

            .v-data-footer__pagination
                margin: 0 2px !important
                font-size: 12px
                flex-shrink: 0

            .v-data-footer__icons-before,
            .v-data-footer__icons-after
                margin: 0 !important
                flex-shrink: 0

                .v-btn
                    width: 32px
                    height: 32px
                    margin: 0 !important

@media (max-width: 420px)
    .usr
        gap: 6px

        &__nm
            font-size: 11px

    .role
        font-size: 9px

    .user-table
        ::v-deep
            thead th
                font-size: 9px !important
                padding: 8px 6px !important

            tbody td
                padding: 8px 6px !important

            thead th:first-child:not(.user-col-name):not(.user-col-role):not(.user-col-actions),
            tbody td:first-child:not(.user-col-name):not(.user-col-role):not(.user-col-actions)
                width: 40px !important
                min-width: 40px !important
                max-width: 40px !important
                padding: 6px 2px 6px 8px !important

            .user-col-role
                width: 96px

            .user-col-actions
                width: 36px
                min-width: 36px
                max-width: 36px
                padding-right: 4px !important

            .v-data-footer
                padding: 6px 6px !important

            .v-data-footer__select
                font-size: 11px

                .v-input,
                .v-select,
                .v-select__selections
                    font-size: 11px

            .v-data-footer__pagination
                font-size: 11px
                margin: 0 1px !important

            .v-data-footer__icons-before,
            .v-data-footer__icons-after
                .v-btn
                    width: 28px
                    height: 28px
</style>
